import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { getTemplateDDEActions, getDDEDetailsForTemplate, getFieldStateById, setFieldState } from 'graphql/operations';
import { triggerDde } from 'dde/triggerDde';
import { gql } from 'graphql.macro';
import { CACHE_ONLY } from 'graphql/fetchPolicies';
import { isSameValue, generateTemplateUpdateVariables, getQuestionValue } from './helper';

const SAVE_DEBOUNCE_TIME = 1500;

function SaveTemplateAnswerHOC(WrappedComponent) {
  return class SaveTemplateAnswer extends Component {
    static propTypes = {
      // TODO: Fix this...
      /* eslint-disable react/forbid-prop-types */
      client: PropTypes.object.isRequired,
      assessmentId: PropTypes.string.isRequired,
      recordId: PropTypes.string.isRequired,
      question: PropTypes.object.isRequired,
      fieldMutation: PropTypes.func.isRequired
      /* eslint-enable react/forbid-prop-types */
    };

    constructor(props) {
      super(props);
      this.save = _.debounce(this.save, SAVE_DEBOUNCE_TIME, { leading: false, trailing: true });
    }

    setFieldState = variables => {
      const { client } = this.props;
      return client.mutate({
        mutation: setFieldState,
        variables,
        refetchQueries: ['listFieldsState']
      });
    };

    save = async (value, otherText) => {
      const { assessmentId: templateId, recordId, question, client, fieldMutation, invokeActionsForFields, templateDdeActionsTriggered } = this.props;
      const { id: fieldId, fieldType } = question;

      const sameValue = isSameValue(question, value, otherText);
      if (sameValue) return;

      const updateTime = moment().toISOString();
      const mutationArgs = {
        id: fieldId,
        loading: true,
        lastUpdated: updateTime,
        fieldType,
        variables: {
          value,
          otherText
        }
      };

      await this.setFieldState(mutationArgs);

      let prevDDEActions;
      let actions;
      let rulesExecutionResult;
      let prevSetActionsValues;

      if (invokeActionsForFields.includes(fieldId)) {
        const ddeDetails = await client.query({
          query: getDDEDetailsForTemplate,
          variables: { templateId, recordId },
          fetchPolicy: CACHE_ONLY
        });
        const dde = ddeDetails.data.getDDEDetailsForTemplate;
        rulesExecutionResult = triggerDde(dde.rules, dde.actions, dde.fields, dde.filterValuesListFields, this.getFieldDetails(question, value));
        actions = {
          invokeActionsForFields: rulesExecutionResult.resultActions.invokeActionsForFields,
          sectionActions: rulesExecutionResult.resultActions.sectionActions,
          fieldActions: rulesExecutionResult.resultActions.fieldActions,
          __typename: 'AclFvlAction'
        };

        prevDDEActions = await client.query({
          query: getTemplateDDEActions,
          variables: { templateId, recordId },
          fetchPolicy: CACHE_ONLY
        });
        // Update the cache with latest actions on field change
        client.cache.writeQuery({
          query: getTemplateDDEActions,
          variables: { templateId, recordId },
          data: { getTemplateDDEActions: actions }
        });
        if (rulesExecutionResult !== null && rulesExecutionResult.setActions !== null && rulesExecutionResult.setActions.length > 0) {
          prevSetActionsValues = this.setActionsCache(rulesExecutionResult.setActions);
        }
      }

      fieldMutation(generateTemplateUpdateVariables(templateId, recordId, fieldId, value, otherText))
        .then(async () => {
          if (invokeActionsForFields.includes(fieldId)) {
            if (rulesExecutionResult !== null && rulesExecutionResult.setActions !== null && rulesExecutionResult.setActions.length > 0) {
              prevSetActionsValues = this.setActionsCache(rulesExecutionResult.setActions);
            }
            const { setActions } = rulesExecutionResult;
            templateDdeActionsTriggered({ variables: { templateId, recordId, setActions } }).catch(() => {
              // In case field save fails, revert the setActions cache to previous state
              if (prevSetActionsValues) {
                this.setPrevActionValues(prevSetActionsValues);
              }
            });
          }
        })
        .catch(error => {
          console.error('save error', fieldId, fieldType, value, otherText, error);
          if (invokeActionsForFields.includes(fieldId)) {
            // In case field save fails, revert the getDDEActions cache to previous state
            client.cache.writeQuery({
              query: getTemplateDDEActions,
              variables: { templateId, recordId },
              data: { getTemplateDDEActions: prevDDEActions.data.getTemplateDDEActions }
            });

            // In case field save fails, revert the setActions cache to previous state
            if (prevSetActionsValues) {
              this.setPrevActionValues(prevSetActionsValues);
            }
          }
        })
        .finally(async () => {
          const result = await client.query({ query: getFieldStateById, variables: { id: fieldId } });
          const fieldState = result.data.getFieldStateById;
          if (updateTime === fieldState.lastUpdated) {
            const updatedMutationArgs = {
              ...mutationArgs,
              loading: false
            };
            await this.setFieldState(updatedMutationArgs);
          }
        });
    };

    getFieldDetails = (question, value) => {
      const { id, fieldType } = question;
      const oldValue = getQuestionValue(question);
      let fieldDetails = {};
      fieldDetails = { fieldID: id, fieldType, newValue: value, oldValue };
      return fieldDetails;
    };

    setActionsCache = setActions => {
      const { client } = this.props;
      const prevValues = [];
      let PrevFragmentValue;
      setActions.forEach(setAction => {
        try {
          if (setAction.fieldType === 'VALUES_LIST') {
            PrevFragmentValue = client.cache.readFragment({
              id: `TemplateValuesListField:${setAction.portalFieldId}`,
              fragment: gql`
                fragment TemplatePrevValuesListQuestion on TemplateValuesListField {
                  id
                  listValue: value
                }
              `
            });
            prevValues.push({
              portalFieldId: setAction.portalFieldId,
              value: PrevFragmentValue.listValue,
              valueType: 'VALUES_LIST'
            });
            client.cache.writeFragment({
              id: `TemplateValuesListField:${setAction.portalFieldId}`,
              fragment: gql`
                fragment TemplateValuesListQuestion on TemplateValuesListField {
                  value
                  __typename
                }
              `,
              data: {
                value: setAction.select_values !== null && setAction.select_values.length > 0 ? setAction.select_values : [],
                __typename: 'TemplateValuesListField'
              }
            });
          } else if (setAction.fieldType === 'DATE') {
            PrevFragmentValue = client.cache.readFragment({
              id: `TemplateDateField:${setAction.portalFieldId}`,
              fragment: gql`
                fragment TemplatePrevDateQuestion on TemplateDateField {
                  id
                  dateValue: value
                }
              `
            });
            prevValues.push({ portalFieldId: setAction.portalFieldId, value: PrevFragmentValue.value, valueType: 'DATE' });
            client.cache.writeFragment({
              id: `TemplateDateField:${setAction.portalFieldId}`,
              fragment: gql`
                fragment TemplateDateQuestion on TemplateDateField {
                  value
                  __typename
                }
              `,
              data: {
                value: setAction.set_date !== null ? new Date(setAction.set_date) : null,
                __typename: 'TemplateDateField'
              }
            });
          }
        } catch (error) {
          // Fragment's value property is not loaded yet. Ignore. Can happen when set actions assigns value to a field that is not loaded yet.
          // Fragment's value needn't be set in this case.
          // Whenever the field will be loaded it will have the latest value from the DB.
        }
      });
      return prevValues;
    };

    setPrevActionValues = prevValues => {
      const { client } = this.props;
      prevValues.forEach(prevValue => {
        try {
          if (prevValue.fieldType === 'VALUES_LIST') {
            client.cache.readFragment({
              id: `TemplateValuesListField:${prevValue.portalFieldId}`,
              fragment: gql`
                fragment TemplatePrevValuesListQuestion on TemplateValuesListField {
                  id
                  listValue: value
                }
              `
            });
            client.cache.writeFragment({
              id: `TemplateValuesListField:${prevValue.portalFieldId}`,
              fragment: gql`
                fragment TemplateValuesListQuestion on TemplateValuesListField {
                  value
                  __typename
                }
              `,
              data: {
                value: prevValue.value !== null && prevValue.value.length > 0 ? new Array(...prevValue.value) : [],
                __typename: 'TemplateValuesListField'
              }
            });
          } else if (prevValue.fieldType === 'DATE') {
            client.cache.readFragment({
              id: `TemplateDateField:${prevValue.portalFieldId}`,
              fragment: gql`
                fragment TemplatePrevDateQuestion on TemplateDateField {
                  id
                  dateValue: value
                }
              `
            });
            client.cache.writeFragment({
              id: `TemplateDateField:${prevValue.portalFieldId}`,
              fragment: gql`
                fragment TemplateDateQuestion on TemplateDateField {
                  value
                  __typename
                }
              `,
              data: {
                value: prevValue.value !== null ? new Date(prevValue.value) : null,
                __typename: 'TemplateDateField'
              }
            });
          }
        } catch (error) {
          // Fragment's value property is not loaded yet. Ignore. Can happen when set actions assigns value to a field that is not loaded yet.
          // Fragment's value needn't be set in this case.
          // Whenever the field will be loaded it will have the latest value from the DB.
        }
      });
    };

    render() {
      return <WrappedComponent {...this.props} save={this.save} />;
    }
  };
}

export default SaveTemplateAnswerHOC;
